<template>
  <b-form-group class="accordion" role="tablist">
    <b-card no-body :class="borderClass">
      <b-card-header header-tag="header" role="tab">
        <div style="cursor: pointer" @click="visible = !visible">PATIENT</div>
      </b-card-header>
      <b-collapse v-model="visible" role="tabpanel">
        <b-card-body>
          <VForm ref="observer" v-slot="{ meta: formMeta }" as="div">
            <form @submit.prevent="savePatient">
              <b-input-group>
                <label
                  v-b-tooltip.hover="
                    'Patient biological sex — used for Cockcroft-Gault renal clearance estimation'
                  "
                  >Gender</label
                >
                <v-select
                  id="gender"
                  v-model="form.gender"
                  placeholder="Select gender"
                  class="w-75"
                  :options="genderList"
                  :clearable="false"
                  aria-required="true"
                  @update:model-value="savePatient"
                ></v-select>
              </b-input-group>
              <b-input-group>
                <label
                  v-b-tooltip.hover="
                    'Patient age — affects renal clearance estimation (Cockcroft-Gault)'
                  "
                  >Age</label
                >
                <VField v-slot="{ meta }" v-model="form.age" name="age" :rules="ageRules">
                  <b-form-input
                    id="age"
                    v-model="form.age"
                    name="age"
                    type="number"
                    :number="true"
                    placeholder="Enter age"
                    :state="getValidationState(meta)"
                    required
                    @change="savePatient"
                  />
                </VField>
                <b-form-select
                  id="ageUnit"
                  ref="ageUnit"
                  v-model="form.ageUnit"
                  class="unit-dropdown"
                  :options="units.age"
                  :clearable="false"
                  required
                  @update:model-value="savePatient"
                >
                </b-form-select>
              </b-input-group>
              <VErrorMessage name="age" as="div" class="invalid-feedback d-block" />
              <b-input-group>
                <label
                  v-b-tooltip.hover="
                    'Patient height — used to calculate body surface area (BSA) and ideal body weight (IBW)'
                  "
                  >Height</label
                >
                <VField v-slot="{ meta }" v-model="form.height" name="height" :rules="heightRules">
                  <b-form-input
                    id="height"
                    v-model="form.height"
                    name="height"
                    type="number"
                    :number="true"
                    class="input-with-unit"
                    placeholder="Enter height"
                    :state="getValidationState(meta)"
                    required
                    @change="savePatient"
                  />
                </VField>
                <b-form-select
                  id="heightUnit"
                  ref="heightUnit"
                  v-model="form.heightUnit"
                  class="unit-dropdown"
                  placeholder="Unit"
                  :options="units.height"
                  :clearable="false"
                  required
                  @update:model-value="savePatient"
                >
                </b-form-select>
              </b-input-group>
              <VErrorMessage name="height" as="div" class="invalid-feedback d-block" />
              <b-input-group>
                <label
                  v-b-tooltip.hover="
                    'Total body weight (TBW) — used for volume of distribution and clearance calculations'
                  "
                  >Weight</label
                >
                <VField v-slot="{ meta }" v-model="form.weight" name="weight" :rules="weightRules">
                  <b-form-input
                    id="weight"
                    v-model="form.weight"
                    name="weight"
                    type="number"
                    :number="true"
                    class="input-with-unit"
                    :state="getValidationState(meta)"
                    placeholder="Enter weight"
                    required
                    @change="savePatient"
                  />
                </VField>
                <b-form-select
                  id="weightUnit"
                  ref="weightUnit"
                  v-model="form.weightUnit"
                  class="unit-dropdown"
                  :options="units.weight"
                  :clearable="false"
                  required
                  @update:model-value="savePatient"
                >
                </b-form-select>
              </b-input-group>
              <VErrorMessage name="weight" as="div" class="invalid-feedback d-block" />
              <b-input-group>
                <label
                  v-b-tooltip.hover="'Body Mass Index — calculated from height and weight (kg/m²)'"
                  >BMI</label
                >
                <b-form-input
                  id="bmi"
                  :model-value="bmi"
                  type="number"
                  class="calculated"
                  readonly
                  disabled
                />
                <div class="input-group-append">&nbsp;</div>
              </b-input-group>
              <b-input-group v-if="store.getModel?.usesDialysis">
                <label
                  v-b-tooltip.hover="
                    'Indicates patient is on intermittent hemodialysis — adjusts clearance in applicable models'
                  "
                  >Hemodialysis</label
                >
                <input
                  id="dialysis"
                  v-model="form.dialysis"
                  type="checkbox"
                  class="form-check-input mt-0"
                  @change="savePatient"
                />
              </b-input-group>
            </form>
          </VForm>
        </b-card-body>
      </b-collapse>
    </b-card>
  </b-form-group>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { genders, units } from '@/models/utils/constants.js';
import { Patient } from '@/models/Patient';
import { defaultPatient } from '@/models/utils/defaults';
import '@/models/utils/validations';
import { useDosingStore } from '@/stores/dosing';

defineOptions({ name: 'PatientInput' });

const props = defineProps<{ patientData?: any }>();

const store = useDosingStore();
const visible = ref(true);
const observer = ref<any>(null);
const genderList = genders;

function initForm(): Patient {
  return props.patientData ? new Patient(props.patientData) : Patient.from(defaultPatient);
}

const form = ref<Patient>(initForm());

watch(
  () => props.patientData,
  () => {
    form.value = initForm();
  },
);

const ageRules = computed(() => ({
  required: true,
  ageBetween: { unit: form.value?.ageUnit, model: store.getModel },
}));

const heightRules = computed(() => ({
  required: true,
  heightBetween: { unit: form.value?.heightUnit, model: store.getModel },
}));

const weightRules = computed(() => ({
  required: true,
  weightBetween: { unit: form.value?.weightUnit, model: store.getModel },
}));

const bmi = computed(() => form.value.bmi());

const borderClass = computed(() => {
  const currentPatient = store.getPatient;
  if (!currentPatient?.isPopulated()) return 'mb-1 border-danger';
  return 'mb-1';
});

function getValidationState(meta: { validated: boolean; valid: boolean }): boolean | null {
  return meta.validated ? meta.valid : null;
}

function savePatient() {
  observer.value.validate().then(({ valid }: { valid: boolean }) => {
    if (valid) store.savePatient(form.value);
  });
}
</script>

<style scoped></style>
